export const RICH_TEXT_PREFIX = "<!--radventura-rich-text:v1-->";

const BLOCK_TAGS = new Set(["P", "DIV", "LI", "H1", "H2", "H3", "H4", "H5", "H6", "BLOCKQUOTE", "PRE"]);

export const isRichTextValue = (value) => String(value || "").startsWith(RICH_TEXT_PREFIX);

const escapeHtml = (value) => String(value || "")
  .replace(/&/g, "&amp;")
  .replace(/</g, "&lt;")
  .replace(/>/g, "&gt;")
  .replace(/"/g, "&quot;")
  .replace(/'/g, "&#039;");

export const plainTextToHtml = (value) => {
  const text = String(value || "").replace(/\r\n?/g, "\n");
  if (!text) return "<p></p>";
  return text.split("\n").map((line) => `<p>${escapeHtml(line) || "<br>"}</p>`).join("");
};
export const richTextToHtml = (value) => {
  const source = String(value || "");
  return isRichTextValue(source) ? source.slice(RICH_TEXT_PREFIX.length) : plainTextToHtml(source);
};

const collapseRepeatedEmptyParagraphs = (html) => {
  const source = String(html || "<p></p>");
  if (typeof document === "undefined") {
    return source.replace(
      /(<p(?:\s[^>]*)?>(?:\s|<br\s*\/?>|&nbsp;)*<\/p>)(?:\s*<p(?:\s[^>]*)?>(?:\s|<br\s*\/?>|&nbsp;)*<\/p>)+/gi,
      "$1"
    );
  }

  const container = document.createElement("div");
  container.innerHTML = source;
  let previousWasEmpty = false;
  Array.from(container.children).forEach((node) => {
    const isEmptyParagraph = node.nodeName === "P" && !String(node.textContent || "").trim();
    if (isEmptyParagraph && previousWasEmpty) {
      node.remove();
      return;
    }
    previousWasEmpty = isEmptyParagraph;
  });
  return container.innerHTML;
};

export const serializeRichText = (html) => `${RICH_TEXT_PREFIX}${collapseRepeatedEmptyParagraphs(html)}`;

const htmlToPlainTextWithDom = (html) => {
  const container = document.createElement("div");
  container.innerHTML = html;
  let output = "";

  const visit = (node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      output += node.nodeValue || "";
      return;
    }
    if (node.nodeName === "BR") {
      output += "\n";
      return;
    }
    const isBlock = BLOCK_TAGS.has(node.nodeName);
    Array.from(node.childNodes || []).forEach(visit);
    if (isBlock && !output.endsWith("\n")) output += "\n";
  };

  Array.from(container.childNodes).forEach(visit);
  return output.replace(/\u00a0/g, " ").replace(/\n{3,}/g, "\n\n").replace(/\n$/, "");
};

export const richTextToPlainText = (value) => {
  const source = String(value || "");
  if (!isRichTextValue(source)) return source;
  const html = source.slice(RICH_TEXT_PREFIX.length);
  if (typeof document !== "undefined" && typeof Node !== "undefined") {
    return htmlToPlainTextWithDom(html);
  }
  return html
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/(p|div|li|h[1-6]|blockquote|pre)>/gi, "\n")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/gi, " ")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&amp;/gi, "&")
    .replace(/&quot;/gi, '"')
    .replace(/&#039;/gi, "'")
    .replace(/\n{3,}/g, "\n\n")
    .replace(/\n$/, "");
};

export const hasRichTextContent = (value) => Boolean(richTextToPlainText(value).trim());

export const copyRichTextToClipboard = async (value) => {
  const plainText = richTextToPlainText(value);
  if (!plainText) return false;
  const canWriteRich = isRichTextValue(value)
    && typeof navigator !== "undefined"
    && navigator.clipboard?.write
    && typeof ClipboardItem !== "undefined";

  if (canWriteRich) {
    await navigator.clipboard.write([new ClipboardItem({
      "text/plain": new Blob([plainText], { type: "text/plain" }),
      "text/html": new Blob([richTextToHtml(value)], { type: "text/html" }),
    })]);
    return true;
  }

  await navigator.clipboard.writeText(plainText);
  return true;
};

export const appendRichText = (current, addition) => {
  if (!hasRichTextContent(addition)) return current;
  if (!hasRichTextContent(current)) return addition;
  if (!isRichTextValue(current) && !isRichTextValue(addition)) return `${current}\n${addition}`;
  return serializeRichText(`${richTextToHtml(current)}${richTextToHtml(addition)}`);
};

export const replaceRichTextBlock = (current, oldBlock, newBlock) => {
  if (!isRichTextValue(current) && !isRichTextValue(oldBlock) && !isRichTextValue(newBlock)) return null;
  let html = richTextToHtml(current);
  if (hasRichTextContent(oldBlock)) html = html.replace(richTextToHtml(oldBlock), "");
  if (hasRichTextContent(newBlock)) {
    html = `${html}${richTextToHtml(newBlock)}`;
  }
  return serializeRichText(html);
};

const normalizeAutomaticValueLines = (value) => String(value || "")
  .replace(/\r\n?/g, "\n")
  .replace(/\s*\|\s*/g, "\n")
  .split("\n")
  .map((line) => line.replace(/^\s*[•*]\s*/, "").trim())
  .filter(Boolean);

export const syncRichTextLabeledLine = (current, label, oldValue, newValue, automaticValues = {}) => {
  if (!isRichTextValue(current) || typeof document === "undefined") return null;
  const container = document.createElement("div");
  container.innerHTML = richTextToHtml(current);
  const children = Array.from(container.children);
  const prefix = `${label}:`;
  const labelNode = children.find((node) => String(node.textContent || "").trim().startsWith(prefix));
  const newLines = normalizeAutomaticValueLines(newValue);

  if (labelNode) {
    const inlineText = String(labelNode.textContent || "").trim().slice(prefix.length).trim();
    const strongLabel = document.createElement("strong");
    strongLabel.textContent = prefix;
    labelNode.replaceChildren(strongLabel);
    const oldLines = normalizeAutomaticValueLines(oldValue);
    let cursor = labelNode.nextElementSibling;
    if (inlineText || oldLines.length) {
      while (cursor && oldLines.includes(String(cursor.textContent || "").trim())) {
        const next = cursor.nextElementSibling;
        cursor.remove();
        cursor = next;
      }
    }
    newLines.forEach((line) => {
      const paragraph = document.createElement("p");
      paragraph.textContent = line;
      labelNode.parentNode.insertBefore(paragraph, cursor);
    });
    while (cursor && !String(cursor.textContent || "").trim()) {
      const next = cursor.nextElementSibling;
      cursor.remove();
      cursor = next;
    }
    if (!newLines.length) labelNode.remove();
  } else if (newLines.length) {
    const fragment = document.createDocumentFragment();
    const labelParagraph = document.createElement("p");
    const strongLabel = document.createElement("strong");
    strongLabel.textContent = prefix;
    labelParagraph.appendChild(strongLabel);
    fragment.appendChild(labelParagraph);
    newLines.forEach((line) => {
      const paragraph = document.createElement("p");
      paragraph.textContent = line;
      fragment.appendChild(paragraph);
    });
    container.insertBefore(fragment, container.firstChild);
  }

  const automaticLabels = new Set(["Klinische Angaben:", "Fragestellung:"]);
  Array.from(container.children).forEach((node) => {
    const text = String(node.textContent || "").trim();
    if (!automaticLabels.has(text) || (node.children.length === 1 && node.firstElementChild?.nodeName === "STRONG")) return;
    const strongLabel = document.createElement("strong");
    strongLabel.textContent = text;
    node.replaceChildren(strongLabel);
  });

  const orderedChildren = Array.from(container.children);
  const clinicalNode = orderedChildren.find((node) => String(node.textContent || "").trim() === "Klinische Angaben:");
  const questionNode = orderedChildren.find((node) => String(node.textContent || "").trim() === "Fragestellung:");
  if (clinicalNode && questionNode && orderedChildren.indexOf(clinicalNode) > orderedChildren.indexOf(questionNode)) {
    const clinicalBlock = document.createDocumentFragment();
    let clinicalCursor = clinicalNode.nextElementSibling;
    clinicalBlock.appendChild(clinicalNode);
    const clinicalLines = normalizeAutomaticValueLines(automaticValues["Klinische Angaben"]);
    clinicalLines.forEach((line) => {
      if (!clinicalCursor || String(clinicalCursor.textContent || "").trim() !== line) return;
      const next = clinicalCursor.nextElementSibling;
      clinicalBlock.appendChild(clinicalCursor);
      clinicalCursor = next;
    });
    if (clinicalCursor && !String(clinicalCursor.textContent || "").trim()) {
      clinicalCursor.remove();
    }
    container.insertBefore(clinicalBlock, questionNode);
  }

  return serializeRichText(container.innerHTML);
};
export const replaceRichTextLine = (current, oldLine, newLine) => {
  if (!isRichTextValue(current) || typeof document === "undefined") return null;
  const container = document.createElement("div");
  container.innerHTML = richTextToHtml(current);
  const children = Array.from(container.children);
  if (oldLine) {
    const oldNode = children.find((node) => String(node.textContent || "").trim() === String(oldLine).trim());
    oldNode?.remove();
  }
  if (newLine) {
    const paragraph = document.createElement("p");
    paragraph.textContent = newLine;
    const currentChildren = Array.from(container.children);
    const firstSpacer = currentChildren.find((node) => !String(node.textContent || "").trim());
    if (firstSpacer) container.insertBefore(paragraph, firstSpacer);
    else container.insertBefore(paragraph, container.firstChild);
  }
  return serializeRichText(container.innerHTML);
};
